import { queryOptions, useQuery } from "@tanstack/react-query"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { PRICEBOOK_ENDPOINTS } from "@endpoints/pricebook"

interface BarcodeSearchPage {
    results: PricebookItemSearchResult[]
}

/** The query for the pricebook item a code is registered to; null when no item in the company has it. */
export function barcodeLookupQueryOptions(searchEndpointTemplate: string, barcode: string, isReady: boolean) {
    const searchEndpoint = `${searchEndpointTemplate}?barcode=${encodeURIComponent(barcode)}`

    return queryOptions({
        queryKey: ["pricebook-item-barcode-lookup", searchEndpoint],
        queryFn: async (): Promise<PricebookItemSearchResult | null> => {
            const response = await fetch(searchEndpoint)

            if (!response.ok) {
                throw new Error(`Request to ${searchEndpoint} failed with status ${response.status}.`)
            }

            // Codes are unique per company, so the page holds at most one item
            const page = (await response.json()) as BarcodeSearchPage
            return page.results[0] ?? null
        },
        enabled: isReady && barcode !== "",
    })
}

/** Look up the pricebook item a code is registered to. */
export default function useBarcodeLookupQuery(barcode: string) {
    const { user } = useUser()
    const [searchEndpointTemplate] = useSlugExtractor([PRICEBOOK_ENDPOINTS.SEARCH])

    return useQuery(barcodeLookupQueryOptions(searchEndpointTemplate, barcode, !!user?.service_company?.slug))
}
